<template>
  <v-row align="center" justify="center">
    <v-col :cols="gridCols">
      <v-card>
        <v-card-text class="m-word-wrap">
          {{ tr('header', 'label') }}
        </v-card-text>
        <v-divider />
        <v-card-text>
          <v-form ref="form" v-model="valid">
            <v-row>
              <v-col>
                <v-select
                  class="m-input-min-width"
                  clearable
                  persistent-hint
                  :items="categoryItems"
                  :label="tr('category', 'label')"
                  :hint="tr('categoryHint', 'label')"
                  :rules="[rules.required]"
                  v-model="category.value"
                >
                  <template v-slot:item="{ item, on, attr }">
                    <v-list-item
                      class="m-list-item-min-height pa-2"
                      v-bind="attr"
                      v-on="on"
                    >
                      <v-list-item-title class="m-word-wrap">
                        {{ item.text }}
                      </v-list-item-title>
                    </v-list-item>
                  </template>
                </v-select>
              </v-col>
              <v-col cols="12">
                <v-textarea
                  class="m-input-min-width"
                  auto-grow
                  clearable
                  counter="2000"
                  :label="tr('text', 'label')"
                  :rules="[rules.required, ruleMax(2000)]"
                  v-model="text"
                />
              </v-col>
              <v-col>
                <v-select
                  class="m-input-min-width"
                  clearable
                  :items="feedbackItems"
                  :label="tr('feedback', 'label')"
                  :rules="[rules.required]"
                  v-model="feedback.value"
                >
                  <template v-slot:item="{ item, on, attr }">
                    <v-list-item
                      class="m-list-item-min-height pa-2"
                      v-bind="attr"
                      v-on="on"
                    >
                      <v-list-item-title class="m-word-wrap">
                        {{ item.text }}
                      </v-list-item-title>
                    </v-list-item>
                  </template>
                </v-select>
              </v-col>
              <v-col>
                <v-text-field
                  class="m-input-min-width"
                  clearable
                  counter="200"
                  :label="tr('remark', 'label')"
                  :rules="[rulePhoneRequired, ruleMax(200)]"
                  v-model="remark"
                />
              </v-col>
              <v-col>
                <v-checkbox
                  color="primary"
                  :label="tr('anonymous', 'label')"
                  v-model="anonymous"
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-row justify="space-between">
            <v-btn text color="primary" @click="$refs.form.reset()">
              {{ tr('reset', 'action') }}
            </v-btn>
            <v-btn
              text
              color="primary"
              :disabled="!valid"
              :loading="loading"
              @click="submit"
            >
              {{ tr('submit', 'action') }}
            </v-btn>
          </v-row>
        </v-card-actions>
      </v-card>
      <legal-info class="pa-3" />
      <div hidden>
        <alert
          :color="alert.color"
          :text="alert.text"
          :timeout="alert.timeout"
          v-model="alert.show"
        />
      </div>
      <v-btn
        small
        fab
        fixed
        bottom
        right
        color="primary"
        v-if="Object.values($route.params).length"
        @click="$router.go(-1)"
      >
        <v-icon large>{{ $mdi.arrowLeft }}</v-icon>
      </v-btn>
    </v-col>
  </v-row>
</template>

<script>
import LegalInfo from '@/components/LegalInfo'
import i18nMixin from '../mixins/i18nMixin'
import AlertMixin from '../mixins/AlertMixin'
export default {
  name: 'HrQuestion',
  components: {
    'legal-info': LegalInfo
  },
  mixins: [i18nMixin, AlertMixin(3000)],
  data () {
    return {
      valid: false,
      loading: false,
      anonymous: false,
      category: {
        value: '',
        list: ['ga', 'labor', 'acc', 'safety', 'news', 'other']
      },
      text: '',
      feedback: {
        value: '',
        list: ['hr', 'manager', 'email', 'phone', 'no']
      },
      remark: '',
      rules: {
        required: v => !!v || this.$t('rules.required')
      }
    }
  },

  computed: {
    gridCols () {
      return this.$vuetify.breakpoint.xl
        ? 4
        : this.$vuetify.breakpoint.lgAndUp
          ? 6
          : 11
    },
    categoryItems () {
      return this.category.list.map(x => ({
        value: x,
        text: this.tr(x, 'item.category')
      }))
    },
    feedbackItems () {
      return this.feedback.list.map(x => ({
        value: x,
        text: this.tr(x, 'item.feedback')
      }))
    },
    rulePhoneRequired () {
      return ['phone', 'email'].includes(this.feedback.value)
        ? this.rules.required
        : v => true
    }
  },

  mounted () {
    const { category, text, feedback, remark, anonymous } = this.$route.params
    this.category.value = this.categoryItems.find(v => v.value === category)
    this.text = text ?? null
    this.feedback.value = this.feedbackItems.find(v => v.value === feedback)
    this.remark = remark ?? null
    this.anonymous = anonymous ?? false
    this.$refs.form.resetValidation()
  },

  methods: {
    ruleMax (qty) {
      return v => !v || v?.length <= qty || this.$tc('rules.max', qty)
    },

    async submit () {
      if (!this.$refs.form.validate()) return
      this.loading = true
      const res = await this.$socket.makeRequest('hrQuestionSave', {
        anonymous: this.anonymous,
        category: this.tr(
          this.category.value,
          'item.category',
          undefined,
          null,
          'ru'
        ),
        categoryCode: this.category.value,
        question: this.text,
        feedback: this.tr(
          this.feedback.value,
          'item.feedback',
          null,
          null,
          'ru'
        ),
        feedbackCode: this.feedback.value,
        remark: this.remark,
        lang: this.$i18n.locale
      })
      this.loading = false
      if (res?.status?.code === 200) {
        this.showInfo = this.tr('success', 'message')
      } else {
        this.showError = this.tr(res?.status?.message, 'message')
      }
    }
  },

  watch: {
    'feedback.value': function () {
      this.$nextTick(() => {
        this.$refs.form?.validate()
      })
    }
  }
}
</script>

<style scoped>
.m-input-min-width {
  min-width: 14em;
}
</style>
